import { Typography } from '@euler/app/components/typography';
import { BlurView } from '@euler/components/BlurView';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AnimatePresence, MotiView } from 'moti';
import { memo } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { DoneButton } from './DoneButton';

type Props = {
  buttonText?: string;
  scale?: number;
  blur?: boolean;
  progressText?: string;
  canNext?: boolean;
  canPrev?: boolean;
  canDone?: boolean;
  onRetake?: () => void;
  onConfirm?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
};

export const TopActionBar = memo(
  ({
    visible,
    scale = 1,
    blur,
    ...props
  }: Props & {
    visible?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    return (
      <AnimatePresence>
        {visible !== false && (
          <MotiView
            nativeID="top-action-bar"
            from={{ translateY: -(rpx(50) + insets.top), opacity: 0 }}
            animate={{ translateY: 0, opacity: 1 }}
            exit={{ translateY: -(rpx(50) + insets.top), opacity: 0 }}
            transition={{
              type: 'timing',
            }}
            css={`
              position: absolute;
              left: 0;
              top: 0;
              right: 0;
              background-color: ${blur ? 'transparent' : '#000'};
            `}
          >
            {blur ? (
              <BlurView
                blurType="extraDark"
                blurAmount={10}
                css={`
                  padding-left: ${rpx(15)}px;
                  padding-right: ${rpx(15)}px;
                  padding-top: ${insets.top + rpx(15)}px;
                  padding-bottom: ${rpx(15)}px;
                `}
              >
                <Content {...props} scale={scale} />
              </BlurView>
            ) : (
              <Content
                {...props}
                scale={scale}
                css={`
                  padding-left: ${rpx(15)}px;
                  padding-right: ${rpx(15)}px;
                  padding-top: ${insets.top + rpx(15)}px;
                  padding-bottom: ${rpx(15)}px;
                `}
              />
            )}
          </MotiView>
        )}
      </AnimatePresence>
    );
  },
);

const Content = memo(
  ({
    buttonText,
    style,
    progressText,
    canNext,
    canPrev,
    canDone = true,
    onRetake,
    onConfirm,
    onNext,
    onPrev,
  }: Props & {
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          align-items: center;
          justify-content: space-between;
          flex-direction: row;
        `}
        style={style}
      >
        <TouchableOpacity onPress={onRetake}>
          <Icon name="arrow-undo-circle-outline" size={rpx(28)} color="white" />
        </TouchableOpacity>
        <View
          css={`
            flex-direction: row;
            justify-content: flex-end;
            align-items: center;
          `}
        >
          {progressText ? (
            <Typography.Label size={rpx(16)} color="#fff">
              {progressText}
            </Typography.Label>
          ) : null}
          {canPrev ? (
            <DoneButton
              scale={rpx(1)}
              text={'上一张'}
              onPress={onPrev}
              css={`
                margin-left: ${rpx(8)}px;
              `}
            />
          ) : undefined}
          {canNext ? (
            <DoneButton
              text={'下一张'}
              scale={rpx(1)}
              onPress={onNext}
              css={`
                margin-left: ${rpx(8)}px;
              `}
            />
          ) : undefined}
          {canDone ? (
            <DoneButton
              text={buttonText ?? '完成'}
              scale={rpx(1)}
              onPress={onConfirm}
              css={`
                margin-left: ${rpx(8)}px;
              `}
            />
          ) : undefined}
        </View>
      </View>
    );
  },
);
